/**
 * Sistema de Temas de Cores para os layouts dos cards
 */

export type PostItColorTheme = 'yellow' | 'pink' | 'blue' | 'green' | 'orange' | 'purple' | 'red' | 'gray' | 'white' | 'darkblue' | 'darkgreen' | 'darkyellow' | 'darkred' | 'gray-10' | 'gray-20' | 'yellow-10' | 'yellow-20' | 'red-10' | 'red-20' | 'green-10' | 'green-20' | 'blue-10' | 'blue-20';
export type DefaultColorTheme = 'emerald' | 'blue' | 'purple' | 'red' | 'orange' | 'teal' | 'yellow' | 'green' | 'lightgreen' | 'darkgreen' | 'lightblue' | 'darkblue' | 'darkyellow' | 'darkred' | 'gray' | 'slate' | 'pink' | 'rose' | 'amber' | 'gray-10' | 'gray-20' | 'yellow-10' | 'yellow-20' | 'red-10' | 'red-20' | 'green-10' | 'green-20' | 'blue-10' | 'blue-20';
export type MinimalColorTheme = 'ocean' | 'forest' | 'sunset' | 'lavender' | 'coral' | 'midnight' | 'sage' | 'amber' | 'emerald' | 'blue' | 'purple' | 'red' | 'orange' | 'teal' | 'yellow' | 'green' | 'lightgreen' | 'darkgreen' | 'lightblue' | 'darkblue' | 'darkyellow' | 'darkred' | 'gray' | 'slate' | 'pink' | 'rose' | 'white' | 'gray-10' | 'gray-20' | 'yellow-10' | 'yellow-20' | 'red-10' | 'red-20' | 'green-10' | 'green-20' | 'blue-10' | 'blue-20';

// Funções auxiliares para conversão de cores
const hexToRgb = (hex: string): { r: number; g: number; b: number } | null => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null;
};

const rgbToHsl = (r: number, g: number, b: number): { h: number; s: number; l: number } => {
  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0, s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
    }
  }

  return { h: h * 360, s: s * 100, l: l * 100 };
};

const hslToRgb = (h: number, s: number, l: number): { r: number; g: number; b: number } => {
  h /= 360;
  s /= 100;
  l /= 100;

  let r, g, b;

  if (s === 0) {
    r = g = b = l;
  } else {
    const hue2rgb = (p: number, q: number, t: number) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1/6) return p + (q - p) * 6 * t;
      if (t < 1/2) return q;
      if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
      return p;
    };

    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1/3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1/3);
  }

  return {
    r: Math.round(r * 255),
    g: Math.round(g * 255),
    b: Math.round(b * 255)
  };
};

const rgbToHex = (r: number, g: number, b: number): string => {
  return '#' + [r, g, b].map(x => {
    const hex = x.toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  }).join('');
};

/**
 * Converte uma cor para ter saturação consistente mantendo o lightness original
 * Mantém o matiz (hue) e lightness da cor original, mas ajusta a saturação
 * Isso garante que cores escuras permaneçam escuras e cores claras permaneçam claras
 * 
 * Para cores acromáticas (preto, branco, cinza), usa o matiz do emerald (160°) como fallback
 */
export const convertToEmeraldParams = (hex: string): string => {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  
  const hsl = rgbToHsl(rgb.r, rgb.g, rgb.b);
  
  // Se a cor for acromática (saturação muito baixa), usar o matiz do emerald como fallback
  // Caso contrário, manter o matiz da cor original
  const hue = hsl.s < 10 ? 160 : hsl.h; // 160° é o matiz do emerald
  
  // Manter o lightness original para preservar cores escuras/claras
  // Aplicar saturação de 84% para consistência visual (mesma saturação do emerald)
  const newRgb = hslToRgb(hue, 84, hsl.l);
  return rgbToHex(newRgb.r, newRgb.g, newRgb.b);
};

export interface ColorTheme {
  bg: string;           // Cor de fundo principal
  border: string;       // Cor da borda
  headerBg?: string;    // Cor do cabeçalho (para default)
  footerBg?: string;    // Cor do rodapé (para postit)
  text: string;         // Cor do texto principal
  textSecondary?: string; // Cor do texto secundário
  accent?: string;       // Cor de destaque (para minimal)
}

// Paletas para Layout Post-it
export const postItThemes: Record<PostItColorTheme, ColorTheme> = {
  yellow: {
    bg: '#fff9c4',
    border: '#e3d081',
    footerBg: '#fff59d',
    text: '#1f2937',
    textSecondary: '#64748b',
  },
  pink: {
    bg: '#fce7f3',
    border: '#f9a8d4',
    footerBg: '#fbcfe8',
    text: '#1f2937',
    textSecondary: '#64748b',
  },
  blue: {
    bg: '#dbeafe',
    border: '#93c5fd',
    footerBg: '#bfdbfe',
    text: '#1f2937',
    textSecondary: '#64748b',
  },
  green: {
    bg: '#dcfce7',
    border: '#86efac',
    footerBg: '#bbf7d0',
    text: '#1f2937',
    textSecondary: '#64748b',
  },
  orange: {
    bg: '#fed7aa',
    border: '#fdba74',
    footerBg: '#fbbf24',
    text: '#1f2937',
    textSecondary: '#64748b',
  },
  purple: {
    bg: '#e9d5ff',
    border: '#c084fc',
    footerBg: '#d8b4fe',
    text: '#1f2937',
    textSecondary: '#64748b',
  },
  red: {
    bg: '#fee2e2',
    border: '#f87171',
    footerBg: '#fecaca',
    text: '#1f2937',
    textSecondary: '#64748b',
  },
  darkred: {
    bg: '#fecaca',
    border: '#dc2626',
    footerBg: '#f87171',
    text: '#1f2937',
    textSecondary: '#64748b',
  },
  darkyellow: {
    bg: '#fef08a',
    border: '#ca8a04',
    footerBg: '#facc15',
    text: '#1f2937',
    textSecondary: '#64748b',
  },
  darkblue: {
    bg: '#93c5fd',
    border: '#1e40af',
    footerBg: '#60a5fa',
    text: '#1f2937',
    textSecondary: '#64748b',
  },
  darkgreen: {
    bg: '#86efac',
    border: '#047857',
    footerBg: '#4ade80',
    text: '#1f2937',
    textSecondary: '#64748b',
  },
  gray: {
    bg: '#f3f4f6',
    border: '#6b7280',
    footerBg: '#e5e7eb',
    text: '#1f2937',
    textSecondary: '#64748b',
  },
  'gray-10': {
    bg: '#f9fafb',
    border: '#d1d5db',
    footerBg: '#f3f4f6',
    text: '#1f2937',
    textSecondary: '#6b7280',
  },
  'gray-20': {
    bg: '#f3f4f6',
    border: '#9ca3af',
    footerBg: '#e5e7eb',
    text: '#1f2937',
    textSecondary: '#4b5563',
  },
  'yellow-10': {
    bg: '#fefce8',
    border: '#fde047',
    footerBg: '#fef9c3',
    text: '#1f2937',
    textSecondary: '#64748b',
  },
  'yellow-20': {
    bg: '#fef08a',
    border: '#eab308',
    footerBg: '#fde047',
    text: '#1f2937',
    textSecondary: '#64748b',
  },
  'red-10': {
    bg: '#fee2e2',
    border: '#fca5a5',
    footerBg: '#fecaca',
    text: '#1f2937',
    textSecondary: '#64748b',
  },
  'red-20': {
    bg: '#fecaca',
    border: '#f87171',
    footerBg: '#fca5a5',
    text: '#1f2937',
    textSecondary: '#64748b',
  },
  'green-10': {
    bg: '#d1fae5',
    border: '#6ee7b7',
    footerBg: '#a7f3d0',
    text: '#1f2937',
    textSecondary: '#64748b',
  },
  'green-20': {
    bg: '#bbf7d0',
    border: '#4ade80',
    footerBg: '#86efac',
    text: '#1f2937',
    textSecondary: '#64748b',
  },
  'blue-10': {
    bg: '#dbeafe',
    border: '#93c5fd',
    footerBg: '#bfdbfe',
    text: '#1f2937',
    textSecondary: '#64748b',
  },
  'blue-20': {
    bg: '#bfdbfe',
    border: '#60a5fa',
    footerBg: '#93c5fd',
    text: '#1f2937',
    textSecondary: '#64748b',
  },
  white: {
    bg: '#ffffff',
    border: '#000000',
    footerBg: '#e2e8f0',
    text: '#000000',
    textSecondary: '#64748b',
  },
};

// Paletas para Layout Default
// Todas as cores usam os mesmos parâmetros de saturação e lightness do emerald padrão
// Apenas o matiz (hue) muda, garantindo consistência visual de contraste
export const defaultThemes: Record<DefaultColorTheme, ColorTheme> = {
  emerald: {
    bg: '#ffffff',
    border: '#10b981', // HSL: h=160°, s=84%, l=39%
    headerBg: '#10b981',
    text: '#1f2937',
    textSecondary: '#64748b',
  },
  blue: {
    bg: '#ffffff',
    border: convertToEmeraldParams('#3b82f6'), // Mantém matiz azul, aplica s=84%, l=39%
    headerBg: convertToEmeraldParams('#3b82f6'),
    text: '#1f2937',
    textSecondary: '#64748b',
  },
  purple: {
    bg: '#ffffff',
    border: convertToEmeraldParams('#a855f7'), // Mantém matiz roxo, aplica s=84%, l=39%
    headerBg: convertToEmeraldParams('#a855f7'),
    text: '#1f2937',
    textSecondary: '#64748b',
  },
  red: {
    bg: '#ffffff',
    border: convertToEmeraldParams('#ef4444'), // Mantém matiz vermelho, aplica s=84%, l=39%
    headerBg: convertToEmeraldParams('#ef4444'),
    text: '#1f2937',
    textSecondary: '#64748b',
  },
  orange: {
    bg: '#ffffff',
    border: convertToEmeraldParams('#f97316'), // Mantém matiz laranja, aplica s=84%, l=39%
    headerBg: convertToEmeraldParams('#f97316'),
    text: '#1f2937',
    textSecondary: '#64748b',
  },
  teal: {
    bg: '#ffffff',
    border: convertToEmeraldParams('#14b8a6'), // Mantém matiz teal, aplica s=84%, l=39%
    headerBg: convertToEmeraldParams('#14b8a6'),
    text: '#1f2937',
    textSecondary: '#64748b',
  },
  yellow: {
    bg: '#ffffff',
    border: convertToEmeraldParams('#eab308'), // Amarelo
    headerBg: convertToEmeraldParams('#eab308'),
    text: '#1f2937',
    textSecondary: '#64748b',
  },
  green: {
    bg: '#ffffff',
    border: convertToEmeraldParams('#22c55e'), // Verde
    headerBg: convertToEmeraldParams('#22c55e'),
    text: '#1f2937',
    textSecondary: '#64748b',
  },
  lightgreen: {
    bg: '#ffffff',
    border: convertToEmeraldParams('#4ade80'), // Verde claro
    headerBg: convertToEmeraldParams('#4ade80'),
    text: '#1f2937',
    textSecondary: '#64748b',
  },
  darkgreen: {
    bg: '#ffffff',
    border: convertToEmeraldParams('#15803d'), // Verde escuro
    headerBg: convertToEmeraldParams('#15803d'),
    text: '#1f2937',
    textSecondary: '#64748b',
  },
  lightblue: {
    bg: '#ffffff',
    border: convertToEmeraldParams('#60a5fa'), // Azul claro
    headerBg: convertToEmeraldParams('#60a5fa'),
    text: '#1f2937',
    textSecondary: '#64748b',
  },
  darkblue: {
    bg: '#ffffff',
    border: convertToEmeraldParams('#1e40af'), // Azul escuro
    headerBg: convertToEmeraldParams('#1e40af'),
    text: '#1f2937',
    textSecondary: '#64748b',
  },
  gray: {
    bg: '#ffffff',
    border: convertToEmeraldParams('#6b7280'), // Cinza
    headerBg: convertToEmeraldParams('#6b7280'),
    text: '#1f2937',
    textSecondary: '#64748b',
  },
  slate: {
    bg: '#ffffff',
    border: convertToEmeraldParams('#475569'), // Slate
    headerBg: convertToEmeraldParams('#475569'),
    text: '#1f2937',
    textSecondary: '#64748b',
  },
  pink: {
    bg: '#ffffff',
    border: convertToEmeraldParams('#ec4899'), // Rosa
    headerBg: convertToEmeraldParams('#ec4899'),
    text: '#1f2937',
    textSecondary: '#64748b',
  },
  rose: {
    bg: '#ffffff',
    border: convertToEmeraldParams('#f43f5e'), // Rose
    headerBg: convertToEmeraldParams('#f43f5e'),
    text: '#1f2937',
    textSecondary: '#64748b',
  },
  amber: {
    bg: '#ffffff',
    border: convertToEmeraldParams('#f59e0b'), // Âmbar
    headerBg: convertToEmeraldParams('#f59e0b'),
    text: '#1f2937',
    textSecondary: '#64748b',
  },
  darkyellow: {
    bg: '#ffffff',
    border: convertToEmeraldParams('#ca8a04'), // Amarelo escuro
    headerBg: convertToEmeraldParams('#ca8a04'),
    text: '#1f2937',
    textSecondary: '#64748b',
  },
  darkred: {
    bg: '#ffffff',
    border: convertToEmeraldParams('#991b1b'), // Vermelho escuro
    headerBg: convertToEmeraldParams('#991b1b'),
    text: '#1f2937',
    textSecondary: '#64748b',
  },
  // Tons intermediários (-10 = muito claro, -20 = claro)
  'gray-10': {
    bg: '#ffffff',
    border: '#d1d5db',
    headerBg: '#d1d5db',
    text: '#1f2937',
    textSecondary: '#64748b',
  },
  'gray-20': {
    bg: '#ffffff',
    border: '#9ca3af',
    headerBg: '#9ca3af',
    text: '#1f2937',
    textSecondary: '#64748b',
  },
  'yellow-10': {
    bg: '#ffffff',
    border: '#facc15',
    headerBg: '#facc15',
    text: '#1f2937',
    textSecondary: '#64748b',
  },
  'yellow-20': {
    bg: '#ffffff',
    border: '#eab308',
    headerBg: '#eab308',
    text: '#1f2937',
    textSecondary: '#64748b',
  },
  'red-10': {
    bg: '#ffffff',
    border: '#f87171',
    headerBg: '#f87171',
    text: '#1f2937',
    textSecondary: '#64748b',
  },
  'red-20': {
    bg: '#ffffff',
    border: '#ef4444',
    headerBg: '#ef4444',
    text: '#1f2937',
    textSecondary: '#64748b',
  },
  'green-10': {
    bg: '#ffffff',
    border: '#4ade80',
    headerBg: '#4ade80',
    text: '#1f2937',
    textSecondary: '#64748b',
  },
  'green-20': {
    bg: '#ffffff',
    border: '#10b981',
    headerBg: '#10b981',
    text: '#1f2937',
    textSecondary: '#64748b',
  },
  'blue-10': {
    bg: '#ffffff',
    border: '#60a5fa',
    headerBg: '#60a5fa',
    text: '#1f2937',
    textSecondary: '#64748b',
  },
  'blue-20': {
    bg: '#ffffff',
    border: '#3b82f6',
    headerBg: '#3b82f6',
    text: '#1f2937',
    textSecondary: '#64748b',
  },
};

// Paletas para Layout Minimal - Design moderno e criativo
export const minimalThemes: Record<MinimalColorTheme, ColorTheme> = {
  ocean: {
    bg: '#f0f9ff',
    border: '#0ea5e9',
    text: '#0c4a6e',
    textSecondary: '#0284c7',
    accent: '#38bdf8',
  },
  forest: {
    bg: '#f0fdf4',
    border: '#22c55e',
    text: '#14532d',
    textSecondary: '#16a34a',
    accent: '#4ade80',
  },
  sunset: {
    bg: '#fff7ed',
    border: '#f97316',
    text: '#7c2d12',
    textSecondary: '#ea580c',
    accent: '#fb923c',
  },
  lavender: {
    bg: '#faf5ff',
    border: '#a855f7',
    text: '#581c87',
    textSecondary: '#9333ea',
    accent: '#c084fc',
  },
  coral: {
    bg: '#fff1f2',
    border: '#f43f5e',
    text: '#881337',
    textSecondary: '#e11d48',
    accent: '#fb7185',
  },
  midnight: {
    bg: '#f1f5f9',
    border: '#475569',
    text: '#0f172a',
    textSecondary: '#334155',
    accent: '#64748b',
  },
  sage: {
    bg: '#f6fef9',
    border: '#6ee7b7',
    text: '#064e3b',
    textSecondary: '#10b981',
    accent: '#34d399',
  },
  amber: {
    bg: '#fffbeb',
    border: '#f59e0b',
    text: '#78350f',
    textSecondary: '#d97706',
    accent: '#fbbf24',
  },
  emerald: {
    bg: '#ecfdf5',
    border: '#10b981',
    text: '#064e3b',
    textSecondary: '#059669',
    accent: '#34d399',
  },
  blue: {
    bg: '#eff6ff',
    border: '#3b82f6',
    text: '#1e3a8a',
    textSecondary: '#2563eb',
    accent: '#60a5fa',
  },
  purple: {
    bg: '#f5f3ff',
    border: '#8b5cf6',
    text: '#4c1d95',
    textSecondary: '#7c3aed',
    accent: '#a78bfa',
  },
  red: {
    bg: '#fef2f2',
    border: '#ef4444',
    text: '#7f1d1d',
    textSecondary: '#dc2626',
    accent: '#f87171',
  },
  orange: {
    bg: '#fff7ed',
    border: '#f97316',
    text: '#7c2d12',
    textSecondary: '#ea580c',
    accent: '#fb923c',
  },
  teal: {
    bg: '#f0fdfa',
    border: '#14b8a6',
    text: '#134e4a',
    textSecondary: '#0f766e',
    accent: '#2dd4bf',
  },
  yellow: {
    bg: '#fefce8',
    border: '#eab308',
    text: '#713f12',
    textSecondary: '#ca8a04',
    accent: '#facc15',
  },
  green: {
    bg: '#f0fdf4',
    border: '#22c55e',
    text: '#14532d',
    textSecondary: '#16a34a',
    accent: '#4ade80',
  },
  lightgreen: {
    bg: '#f7fee7',
    border: '#84cc16',
    text: '#365314',
    textSecondary: '#65a30d',
    accent: '#a3e635',
  },
  darkgreen: {
    bg: '#ecfdf5',
    border: '#047857',
    text: '#064e3b',
    textSecondary: '#065f46',
    accent: '#10b981',
  },
  lightblue: {
    bg: '#f0f9ff',
    border: '#0ea5e9',
    text: '#0c4a6e',
    textSecondary: '#0284c7',
    accent: '#38bdf8',
  },
  darkblue: {
    bg: '#eff6ff',
    border: '#1e40af',
    text: '#1e3a8a',
    textSecondary: '#1e40af',
    accent: '#3b82f6',
  },
  darkyellow: {
    bg: '#fefce8',
    border: '#ca8a04',
    text: '#713f12',
    textSecondary: '#a16207',
    accent: '#eab308',
  },
  darkred: {
    bg: '#fef2f2',
    border: '#991b1b',
    text: '#7f1d1d',
    textSecondary: '#b91c1c',
    accent: '#dc2626',
  },
  gray: {
    bg: '#f9fafb',
    border: '#6b7280',
    text: '#111827',
    textSecondary: '#4b5563',
    accent: '#9ca3af',
  },
  slate: {
    bg: '#f8fafc',
    border: '#475569',
    text: '#0f172a',
    textSecondary: '#334155',
    accent: '#64748b',
  },
  pink: {
    bg: '#fdf2f8',
    border: '#ec4899',
    text: '#831843',
    textSecondary: '#db2777',
    accent: '#f472b6',
  },
  rose: {
    bg: '#fff1f2',
    border: '#f43f5e',
    text: '#881337',
    textSecondary: '#e11d48',
    accent: '#fb7185',
  },
  white: {
    bg: '#ffffff',
    border: '#000000',
    text: '#000000',
    textSecondary: '#4b5563',
    accent: '#000000',
  },
  // Tons intermediários (-10 = muito claro, -20 = claro)
  'gray-10': {
    bg: '#f9fafb',
    border: '#d1d5db',
    text: '#374151',
    textSecondary: '#6b7280',
    accent: '#e5e7eb',
  },
  'gray-20': {
    bg: '#f3f4f6',
    border: '#9ca3af',
    text: '#1f2937',
    textSecondary: '#4b5563',
    accent: '#d1d5db',
  },
  'yellow-10': {
    bg: '#fefce8',
    border: '#facc15',
    text: '#713f12',
    textSecondary: '#ca8a04',
    accent: '#fde047',
  },
  'yellow-20': {
    bg: '#fef9c3',
    border: '#eab308',
    text: '#713f12',
    textSecondary: '#a16207',
    accent: '#facc15',
  },
  'red-10': {
    bg: '#fef2f2',
    border: '#f87171',
    text: '#7f1d1d',
    textSecondary: '#dc2626',
    accent: '#fca5a5',
  },
  'red-20': {
    bg: '#fee2e2',
    border: '#ef4444',
    text: '#7f1d1d',
    textSecondary: '#b91c1c',
    accent: '#f87171',
  },
  'green-10': {
    bg: '#ecfdf5',
    border: '#4ade80',
    text: '#14532d',
    textSecondary: '#16a34a',
    accent: '#86efac',
  },
  'green-20': {
    bg: '#d1fae5',
    border: '#10b981',
    text: '#064e3b',
    textSecondary: '#059669',
    accent: '#4ade80',
  },
  'blue-10': {
    bg: '#eff6ff',
    border: '#60a5fa',
    text: '#1e3a8a',
    textSecondary: '#2563eb',
    accent: '#93c5fd',
  },
  'blue-20': {
    bg: '#dbeafe',
    border: '#3b82f6',
    text: '#1e3a8a',
    textSecondary: '#1d4ed8',
    accent: '#60a5fa',
  },
};

/**
 * Tema especial para cor branca (aplica contornos pretos em todos os estilos)
 */
const whiteTheme: ColorTheme = {
  bg: '#ffffff',
  border: '#000000',
  headerBg: '#000000',
  footerBg: '#e2e8f0',
  text: '#000000',
  textSecondary: '#64748b',
  accent: '#000000',
};

/**
 * Obtém o tema de cores baseado no layout e na cor selecionada
 */
export function getLayoutTheme(
  layoutType: 'default' | 'postit' | 'minimal',
  colorTheme: string
): ColorTheme | null {
  // Se a cor for 'white', retornar tema branco especial
  if (colorTheme === 'white') {
    return whiteTheme;
  }
  
  switch (layoutType) {
    case 'postit':
      return postItThemes[colorTheme as PostItColorTheme] || postItThemes.yellow;
    case 'default':
      return defaultThemes[colorTheme as DefaultColorTheme] || defaultThemes.emerald;
    case 'minimal':
      return minimalThemes[colorTheme as MinimalColorTheme] || minimalThemes.ocean;
    default:
      return null;
  }
}

/**
 * Obtém todas as cores disponíveis para um layout
 */
export function getAvailableColors(layoutType: 'default' | 'postit' | 'minimal'): string[] {
  switch (layoutType) {
    case 'postit':
      return Object.keys(postItThemes);
    case 'default':
      return Object.keys(defaultThemes);
    case 'minimal':
      return Object.keys(minimalThemes);
    default:
      return [];
  }
}

